import { HttpClient } from '@angular/common/http';
import {
  Component, Inject, Input, OnInit,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ButtonSize, ButtonStyle } from '../../aero/aero-button/aero-button.component';
import { AeroDialogService } from '../../aero/aero-dialog/aero-dialog.service';
import { TranslationService } from '../../common/translation.service';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';
import { AddEmailComponent } from './add-email.component';

/**
 * Component for managing account email settings and operations.
 * Provides functionality to view, set default email, verify emails, add new emails, and remove existing emails.
 * Interacts with authentication, HTTP client, dialog services, translation service, and error handling services.
 */
@Component({
  selector: 'bulb-account-emails',
  templateUrl: './account-emails.component.html',
  styleUrls: ['./account-emails.component.scss'],
  standalone: false,
})
export class AccountEmailsComponent implements OnInit {
  @Input() account: any;

  public emails: any[] = [];

  protected readonly ButtonStyle = ButtonStyle;

  protected readonly ButtonSize = ButtonSize;

  constructor(
    private http: HttpClient,
    private dialog: AeroDialogService,
    private translation: TranslationService,
    @Inject('BulbAuthService') private auth: any,
    @Inject(BULB_BUSY_COVER_SERVICE) private busyService: any,
    @Inject('BulbErrorService') private errorService: any,
  ) {}

  /**
   * The provided code is an Angular component that manages and handles email settings for a user account.
   * It interacts with various services for HTTP requests, dialog management, translation,
   * busy state handling, and error handling. Here's a breakdown of the key functionalities and components:
   */
  ngOnInit(): void {
    this.emails = this.account?.emails || this.auth.currentUser.emails || [];
  }

  /**
   * Check if email is default email.
   * @param email Email to verify
   */
  isDefault(email: any): boolean { return email.email === this.auth.currentUser.defaultEmail; }

  /**
   * Sets the default email for the current user account.
   *
   * @param email - The email object to set as default, containing email address and verification status
   * @return void
   */
  setDefault(email: any): void {
    if (this.isDefault(email)) return;
    if (!email.verified) {
      this.showDialog('account_settings_default_email_error', 'account_settings_default_email_error_body');
      return;
    }
    this.runRequest(
      this.http.post(`accounts/${this.account.id}/setDefaultEmail`, { email: email.email }),
      () => { this.auth.currentUser.defaultEmail = email.email; },
    );
  }

  /**
   * Sends email verification request
   * @param email - Email object containing the email address to verify
   * @param isNew - Flag indicating if this is a new email verification (default: false)
   * @return void
   */
  verify(email: any, isNew = false): void {
    this.runRequest(
      this.http.post(`accounts/${this.account.id}/sendEmailVerification`, { email: email.email }),
      () => this.showDialog(
        isNew ? 'account_settings_add_new_email' : 'account_settings_verify_email_sent',
        isNew ? 'account_settings_add_new_email_body' : 'account_settings_verify_email_resent',
        { email: email.email },
        'fa fa-check-circle',
      ),
    );
  }

  /**
   * Removes an email address from the account settings.
   *
   * @param email - The email address to be removed, typically containing email information
   */
  remove(email: any): void {
    if (!this.canRemove(email)) {
      this.showDialog(
        'account_settings_disconnect_error_subscribed_unverified',
        'account_settings_disconnect_error_subscribed_unverified_body',
      );
      return;
    }
    let detail = this.translation.translateInstant('account_settings_disconnect_remove_email_body')
      .replace('{{email}}', email.email);
    if (this.emails.length <= 1) {
      detail += this.translation.translateInstant('account_settings_disconnect_remove_email_body_last_one');
    }
    this.dialog.showDialog({
      icon: 'fa fa-exclamation-triangle',
      iconClass: 'aero-alert',
      title: this.translation.translateInstant('account_settings_disconnect_remove_email'),
      detail,
      primaryLabel: this.translation.translateInstant('group_member_remove_label'),
      primaryStyle: ButtonStyle.NEGATIVE_ACTION,
      onPrimary: () => this.sendRemove(email),
    });
  }

  /**
   * Check whether email can be removed or not.
   * @param email - The email object to check for removal capability
   * @return true if the email can be removed, false otherwise
   */
  canRemove(email: any): boolean {
    return !this.isDefault(email) && this.emails.some((item) => item.email !== email.email && item.verified);
  }

  /**
   * Checks whether the current user can add data entries.
   * @return {boolean} True if the user can add data entries, false otherwise.
   */
  canAdd(): boolean {
    return !this.auth.currentUser.features?.DataEntryProtectionFeature?.enabled;
  }

  /**
   * Displays a dialog for adding a new email address.
   * @return {void} Returns nothing
   */
  add(): void {
    if (!this.canAdd()) return;
    let email = '';
    this.dialog.showDialog({
      customContentComponent: AddEmailComponent,
      icon: 'far fa-envelope',
      iconClass: 'aero-highlight',
      title: this.translation.translateInstant('account_settings_email_add_email'),
      primaryDisabled: true,
      primaryLabel: this.translation.translateInstant('button_add'),
      data: { valueChange: (value) => { email = value; } },
      onPrimary: () => this.createEmail(email),
    });
  }

  /**
   * Creates a new email address for the account and handles the response.
   *
   * @param email - The email address to be created
   * @return void
   */
  private createEmail(email: string): void {
    this.runRequest(
      this.http.post(`accounts/${this.account.id}/emails`, { email }),
      () => {
        this.emails = [...this.emails, { email, verified: false }];
        this.verify({ email }, true);
      },
      (error) => {
        if (error?.status === 409) {
          this.errorService.showError(this.translation.translateInstant('email_conflict_error'), error);
        } else {
          this.errorService.showError(null, error);
        }
      },
    );
  }

  /**
   * Sends a request to remove an email address from the account.
   * @param email - The email object containing the email address to be removed
   * @return void
   */
  private sendRemove(email: any): void {
    this.runRequest(
      this.http.delete(`accounts/${this.account.id}/emails?email=${email.email}`),
      (response: any) => {
        this.emails = this.emails.filter((item) => item.email !== email.email);
        this.auth.currentUser.defaultEmail = response?.message;
      },
    );
  }

  /**
   * Executes a request and handles the response or error using provided callbacks.
   *
   * @param request - The request to be executed, typically a promise or observable
   * @param onSuccess - Callback function called with the response on successful completion
   * @param onError - Optional callback function called with the error if the request fails
   * @return void
   */
  private runRequest(request: any, onSuccess: (response?: any) => void, onError?: (error: any) => void): void {
    const operation = firstValueFrom(request).then(onSuccess).catch((error) => {
      if (onError) onError(error);
      else this.errorService.showError(null, error);
    });
    this.busyService.showBusyDuring(operation);
  }

  /**
   * Displays a dialog box with the specified title and detail messages.
   *
   * @param titleCode - The translation code for the dialog title
   * @param detailCode - The translation code for the dialog detail message
   * @param values - Optional object containing replacement values for placeholders in the detail message
   * @param icon - Optional icon class name for the dialog, defaults to 'fa fa-exclamation-triangle'
   * @return void
   */
  private showDialog(titleCode: string, detailCode: string, values?: any, icon = 'fa fa-exclamation-triangle'): void {
    let detail = this.translation.translateInstant(detailCode);
    Object.keys(values || {}).forEach((key) => {
      detail = detail.replace(`{{${key}}}`, values[key]);
    });
    this.dialog.showDialog({
      icon,
      iconClass: icon.includes('triangle') ? 'aero-alert' : undefined,
      title: this.translation.translateInstant(titleCode),
      detail,
      primaryLabel: this.translation.translateInstant('button_ok'),
      showSecondary: false,
    });
  }
}
